"use client";

import { useEffect, useRef, type CSSProperties } from "react";

const TRAIL_LENGTH = 14;
const CURSOR_EASE = 0.22;
const TRAIL_EASE = 0.38;

export function CursorGlow() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const trailRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const cursor = cursorRef.current;
    const glow = glowRef.current;
    if (!cursor) return;

    const target = { x: -100, y: -100 };
    const pos = { x: -100, y: -100 };
    const trail = Array.from({ length: TRAIL_LENGTH }, () => ({ x: -100, y: -100 }));
    let raf = 0;

    const setVisible = (show: boolean) => {
      cursor.classList.toggle("show", show);
      glow?.classList.toggle("show", show);
      trailRefs.current.forEach((el) => el?.classList.toggle("show", show));
    };

    const onMove = (e: MouseEvent) => {
      target.x = e.clientX;
      target.y = e.clientY;
      setVisible(true);
    };

    const onLeave = () => setVisible(false);

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    const apply = (el: HTMLElement | null, x: number, y: number) => {
      if (!el) return;
      el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    };

    const tick = () => {
      pos.x = lerp(pos.x, target.x, CURSOR_EASE);
      pos.y = lerp(pos.y, target.y, CURSOR_EASE);

      apply(cursor, pos.x, pos.y);
      apply(glow, pos.x, pos.y);

      let prevX = pos.x;
      let prevY = pos.y;

      for (let i = 0; i < TRAIL_LENGTH; i++) {
        trail[i].x = lerp(trail[i].x, prevX, TRAIL_EASE);
        trail[i].y = lerp(trail[i].y, prevY, TRAIL_EASE);
        apply(trailRefs.current[i], trail[i].x, trail[i].y);
        prevX = trail[i].x;
        prevY = trail[i].y;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    const hoverables = document.querySelectorAll(
      "a, .pcard, .note, .btn, .cta",
    );
    const onEnter = () => {
      cursor.classList.add("big");
      glow?.classList.add("big");
    };
    const onExit = () => {
      cursor.classList.remove("big");
      glow?.classList.remove("big");
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    hoverables.forEach((el) => {
      el.addEventListener("mouseenter", onEnter);
      el.addEventListener("mouseleave", onExit);
    });

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
      hoverables.forEach((el) => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onExit);
      });
    };
  }, []);

  return (
    <div className="cursor-layer" aria-hidden="true">
      {Array.from({ length: TRAIL_LENGTH }, (_, i) => (
        <div
          key={i}
          ref={(el) => {
            trailRefs.current[i] = el;
          }}
          className="cursor-trail-dot"
          style={{ "--i": i } as CSSProperties}
        />
      ))}
      <div ref={glowRef} className="cursor-glow" />
      <div ref={cursorRef} className="cursor" id="cursor" />
    </div>
  );
}
